import React, { useEffect, useMemo, useState } from "react";
import rosterSoccer from "../json/rosterSoccer.json";
import "../css/SoccerGuessPlayer.css";

export default function SoccerGuessThePlayerGame() {
  const players = useMemo(() => {
    if (!rosterSoccer || typeof rosterSoccer !== "object") return [];

    return Object.entries(rosterSoccer).flatMap(([team, teamPlayers]) =>
      (Array.isArray(teamPlayers) ? teamPlayers : []).map((player) => ({
        ...player,
        team,
      }))
    );
  }, []);

  const teamNames = useMemo(() => {
    return [...new Set(players.map((p) => p.team).filter(Boolean))];
  }, [players]);

  const hasMultipleTeams = teamNames.length > 1;

  const getRandomPlayer = () => {
    if (!players.length) return null;
    return players[Math.floor(Math.random() * players.length)];
  };

  const [roundPlayer, setRoundPlayer] = useState(null);
  const [revealedCount, setRevealedCount] = useState(1);
  const [guess, setGuess] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("neutral");
  const [score, setScore] = useState(() => {
  const stored = localStorage.getItem("soccerGuessScore");
  return stored ? Number(stored) : 0;
});

const [bestScore, setBestScore] = useState(() => {
  const stored = localStorage.getItem("soccerGuessBestScore");
  return stored ? Number(stored) : 0;
});

const [roundsWon, setRoundsWon] = useState(() => {
  const stored = localStorage.getItem("soccerGuessRoundsWon");
  return stored ? Number(stored) : 0;
});

const [roundsPlayed, setRoundsPlayed] = useState(() => {
  const stored = localStorage.getItem("soccerGuessRoundsPlayed");
  return stored ? Number(stored) : 0;
});
  const [gameOver, setGameOver] = useState(false);

  const normalizedGuess = (value) =>
    value
      .trim()
      .toLowerCase()
      .replace(/\./g, "")
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 '\-]/g, "");

  const buildClues = (player) => {
    if (!player) return [];

    const clues = [
      { label: "Position", value: player.position || "Unknown" },
      { label: "Jersey number", value: player.number ?? "Unknown" },
    ];

    if (hasMultipleTeams) {
      clues.push({ label: "Team", value: player.team || "Unknown" });
    }

    return clues;
  };

  const clues = useMemo(() => buildClues(roundPlayer), [roundPlayer, hasMultipleTeams]);
  const visibleClues = clues.slice(0, Math.min(revealedCount, clues.length));
  const allNames = useMemo(() => players.map((p) => p.name).filter(Boolean), [players]);

  const filteredNames = useMemo(() => {
  const q = guess.trim().toLowerCase();
  if (!q) return [];

  return allNames
    .filter((name) => {
      const lower = name.toLowerCase();
      return lower.includes(q) && lower !== q;
    })
    .slice(0, 8);
}, [guess, allNames]);

  function startNewRound() {
    const nextPlayer = getRandomPlayer();
    setRoundPlayer(nextPlayer);
    setRevealedCount(1);
    setGuess("");
    setMessage("");
    setMessageType("neutral");
    setGameOver(false);
  }

  useEffect(() => {
    startNewRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players.length]);

  useEffect(() => {
    localStorage.setItem("soccerGuessBestScore", String(bestScore));
  }, [bestScore]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!roundPlayer) return;

    const cleanGuess = normalizedGuess(guess);
    const cleanAnswer = normalizedGuess(roundPlayer.name || "");

    if (!cleanGuess) return;

    if (cleanGuess === cleanAnswer) {
      const points = Math.max(1, clues.length - revealedCount + 1);
      const nextScore = score + points;

      setScore(nextScore);
      setRoundsWon((current) => current + 1);
      setRoundsPlayed((current) => current + 1);
      setBestScore((current) => Math.max(current, nextScore));
      setGameOver(true);
      setMessage(`Correct — ${roundPlayer.name} (${points} point${points === 1 ? "" : "s"}).`);
      setMessageType("success");
      return;
    }

    if (revealedCount < clues.length) {
      setRevealedCount((current) => current + 1);
      setMessage("Not quite. Another clue unlocked.");
      setMessageType("warning");
    } else {
      setRoundsPlayed((current) => current + 1);
      setMessage(`Wrong — it was ${roundPlayer.name}.`);
      setMessageType("danger");
      setGameOver(true);
    }
  }

  function handleNextRound() {
    startNewRound();
  }

  function handleRestart() {
    setScore(0);
    setRoundsWon(0);
    setRoundsPlayed(0);
    startNewRound();
  }

  if (!players.length) {
    return (
      <div className="guess-card">
        <div className="guess-header">
          <p className="guess-eyebrow">Premier League Mini Game</p>
          <h2>Guess the Player</h2>
        </div>
        <p className="guess-empty">No roster data was found.</p>
      </div>
    );
  }

  return (
    <div className="guess-card">
      <div className="guess-header">
        <p className="guess-eyebrow">Premier League Mini Game</p>
        <h2>Guess the Player</h2>
      </div>

      <p className="guess-subtitle">
        Use the clues to name the player. The fewer clues you need, the more points you earn.
      </p>

      <div className="guess-stats">
        <div className="guess-stat">
          <span className="guess-stat-label">Highest Streak</span>
          <span className="guess-stat-value">{bestScore}</span>
        </div>
        <div className="guess-stat">
          <span className="guess-stat-label">Current Streak</span>
          <span className="guess-stat-value">{roundsWon}</span>
        </div>
        <div className="guess-stat">
          <span className="guess-stat-label">Played</span>
          <span className="guess-stat-value">{roundsPlayed}</span>
        </div>
      </div>

      <div className="guess-round">
        <div className="guess-round-top">
          <div className="guess-pill">Round {roundsPlayed + 1}</div>
        </div>

        <div className="guess-clues">
          {visibleClues.map((clue, index) => (
            <div className="guess-clue" key={`${clue.label}-${index}`}>
              <div className="guess-clue-label">{clue.label}</div>
              <div className="guess-clue-value">{clue.value}</div>
            </div>
          ))}
        </div>

        <form className="guess-form" onSubmit={handleSubmit} autoComplete="off">
  <div className="guess-input-wrap">
    <input
      className="guess-input"
      value={guess}
      onChange={(e) => setGuess(e.target.value)}
      placeholder="Type the player's name"
      autoComplete="off"
    />

    {filteredNames.length > 0 && (
      <div className="guess-suggestions">
        {filteredNames.map((name) => (
          <button
            key={name}
            type="button"
            className="guess-suggestion"
            onClick={() => setGuess(name)}
          >
            {name}
          </button>
        ))}
        </div>
        )}
    </div>

        <button className="guess-button" type="submit" disabled={!roundPlayer || gameOver}>
            Submit Guess
        </button>
    </form>

        <div className={`guess-feedback ${messageType}`}>{message || "Make a guess to start."}</div>

        <div className="guess-actions">
          {!gameOver ? (
            <button
              type="button"
              className="guess-button secondary"
              onClick={() => {
                if (revealedCount < clues.length) {
                  setRevealedCount((current) => current + 1);
                  setMessage("Another clue unlocked.");
                  setMessageType("warning");
                } else {
                  setMessage(`No more clues left. It was ${roundPlayer?.name}.`);
                  setMessageType("danger");
                  setGameOver(true);
                }
              }}
            >
              Reveal Next Clue
            </button>
          ) : (
            <button type="button" className="guess-button secondary" onClick={handleNextRound}>
              Next Player
            </button>
          )}

          <button type="button" className="guess-button secondary" onClick={handleRestart}>
            Restart Game
          </button>
        </div>
      </div>
    </div>
  );
}